import * as React from 'react';
import {
  ArrowDoubleLeft,
  ArrowDoubleRight,
  ArrowDown,
  ArrowLeft,
  ArrowRight,
} from '@youlify/icons';
import classNames from 'classnames';
import type { PaginationLocale, PaginationProps as RcPaginationProps } from 'rc-pagination';
import RcPagination from 'rc-pagination';
import enUS from 'rc-pagination/lib/locale/en_US';

import { devUseWarning } from '../_util/warning';
import { useComponentConfig } from '../config-provider/context';
import useSize from '../config-provider/hooks/useSize';
import useBreakpoint from '../grid/hooks/useBreakpoint';
import InputNumber from '../input-number';
import { useLocale } from '../locale';
import type { SelectProps } from '../select';
import Select from '../select';
import { useToken } from '../theme/internal';
import * as colorPalette from '../theme/themes/colorPalette';
import useStyle from './style';
import BorderedStyle from './style/bordered';
import useShowSizeChanger from './useShowSizeChanger';

export interface PaginationProps
  extends Omit<RcPaginationProps, 'showSizeChanger' | 'pageSizeOptions'> {
  showQuickJumper?: boolean | { goButton?: React.ReactNode };
  size?: 'default' | 'small';
  responsive?: boolean;
  role?: string;
  totalBoundaryShowSizeChanger?: number;
  rootClassName?: string;
  showSizeChanger?: boolean | SelectProps;
  /** @deprecated Not official support. Will be removed in next major version. */
  selectComponentClass?: any;
  /** `string` type will be removed in next major version. */
  pageSizeOptions?: (string | number)[];
}

export type PaginationPosition = 'top' | 'bottom' | 'both';

export interface PaginationConfig extends Omit<PaginationProps, 'rootClassName'> {
  position?: PaginationPosition;
}

export type { PaginationLocale };

const Pagination: React.FC<PaginationProps> = (props) => {
  const {
    align,
    prefixCls: customizePrefixCls,
    selectPrefixCls: customizeSelectPrefixCls,
    className,
    rootClassName,
    style,
    size: customizeSize,
    locale: customLocale,
    responsive,
    showSizeChanger = true,
    selectComponentClass,
    pageSizeOptions,
    total = 0,
    defaultCurrent = 1,
    defaultPageSize = 20,
    current: controlledCurrent,
    pageSize: controlledPageSize,
    onChange,
    onShowSizeChange,
    showQuickJumper = true,
    ...restProps
  } = props;
  const { xs } = useBreakpoint(responsive);
  const [, token] = useToken();
  const [innerCurrent, setInnerCurrent] = React.useState(defaultCurrent);
  const [innerPageSize, setInnerPageSize] = React.useState(defaultPageSize);

  const isControlled = controlledCurrent !== undefined || controlledPageSize !== undefined;

  const mergedCurrent = controlledCurrent ?? innerCurrent;
  const mergedPageSize = controlledPageSize ?? innerPageSize;

  const {
    getPrefixCls,
    direction,
    showSizeChanger: contextShowSizeChangerConfig,
    className: contextClassName,
    style: contextStyle,
  } = useComponentConfig('pagination');
  const prefixCls = getPrefixCls('pagination', customizePrefixCls);

  React.useEffect(() => {
    const max = Math.max(1, Math.ceil(total / mergedPageSize));
    if (!isControlled && mergedCurrent > max) {
      setInnerCurrent(max);
    }
  }, [total, mergedPageSize, mergedCurrent, isControlled]);

  const maxPage = Math.max(1, Math.ceil(total / mergedPageSize));

  const handlePageSizeChange = (size: number) => {
    onShowSizeChange?.(1, size);
    onChange?.(1, size);

    if (controlledCurrent === undefined) {
      setInnerCurrent(1);
    }
    if (controlledPageSize === undefined) {
      setInnerPageSize(size);
    }
  };

  const handlePageChange = (page: number, size?: number) => {
    const nextSize = size ?? mergedPageSize;

    if (controlledCurrent === undefined) {
      setInnerCurrent(page);
    }
    if (controlledPageSize === undefined && size && size !== mergedPageSize) {
      setInnerPageSize(size);
    }

    onChange?.(page, nextSize);
  };

  const safeGo = (page: number) => {
    const safePage = Math.min(Math.max(1, page), maxPage);
    handlePageChange(safePage);
  };

  // Style
  const [wrapCSSVar, hashId, cssVarCls] = useStyle(prefixCls);

  // ============================== Size ==============================
  const mergedSize = useSize(customizeSize);

  const isSmall = mergedSize === 'small' || !!(xs && !mergedSize && responsive);

  // ============================= Locale =============================
  const [contextLocale] = useLocale('Pagination', enUS);

  const locale = { ...contextLocale, ...customLocale };

  // ========================== Size Changer ==========================
  // Merge the props showSizeChanger
  const [propShowSizeChanger, propSizeChangerSelectProps] = useShowSizeChanger(showSizeChanger);
  const [contextShowSizeChanger, contextSizeChangerSelectProps] = useShowSizeChanger(
    contextShowSizeChangerConfig,
  );

  const mergedShowSizeChanger = propShowSizeChanger ?? contextShowSizeChanger;
  const mergedShowSizeChangerSelectProps =
    propSizeChangerSelectProps ?? contextSizeChangerSelectProps;

  const SizeChanger: typeof Select = selectComponentClass || Select;

  // Generate options
  const mergedPageSizeOptions = React.useMemo(() => {
    return pageSizeOptions ? pageSizeOptions.map((option) => Number(option)) : [10, 20, 50, 100];
  }, [pageSizeOptions]);

  // Render size changer
  const sizeChangerRender: RcPaginationProps['sizeChangerRender'] = (info) => {
    const {
      disabled,
      size: pageSize,
      onSizeChange,
      'aria-label': ariaLabel,
      className: sizeChangerClassName,
      options,
    } = info;
    const [open, setOpen] = React.useState(false);

    const { className: propSizeChangerClassName, onChange: propSizeChangerOnChange } =
      mergedShowSizeChangerSelectProps || {};

    // Origin Select is using Select.Option,
    // So it make the option value must be string
    // Just for compatible
    const selectedValue = options.find(
      (option) => String(option.value) === String(pageSize),
    )?.value;

    return (
      <div className={`${prefixCls}-custom-size-changer`}>
        <span
          className={`${prefixCls}-custom-size-changer-label`}
          style={{
            fontSize: token.fontSizeSM,
            fontWeight: 500,
            color: colorPalette.gray[600],
            marginRight: token.marginSM,
          }}
        >
          Showing per page
        </span>
        <SizeChanger
          disabled={disabled}
          showSearch
          popupMatchSelectWidth={false}
          getPopupContainer={(triggerNode) => triggerNode.parentNode}
          aria-label={ariaLabel}
          options={options.map((opt) => ({ ...opt, label: opt.value }))}
          {...mergedShowSizeChangerSelectProps}
          value={selectedValue}
          onChange={(nextSize, option) => {
            onSizeChange?.(nextSize);
            propSizeChangerOnChange?.(nextSize, option);
          }}
          size={isSmall ? 'small' : 'middle'}
          className={classNames(sizeChangerClassName, propSizeChangerClassName)}
          open={open}
          onDropdownVisibleChange={setOpen}
          suffixIcon={<ArrowDown style={{ cursor: 'pointer' }} onClick={() => setOpen(!open)} />}
          style={{ width: 72 }}
        />
      </div>
    );
  };

  const quickJumperRender = (info: {
    disabled: boolean;
    go: (page: number) => void;
    maxPage: number;
  }) => {
    const { disabled, go, maxPage } = info;
    const [page, setPage] = React.useState<number | null>(null);
    return (
      <div className={`${prefixCls}-custom-quick-jumper`}>
        <span
          style={{
            fontSize: token.fontSizeSM,
            fontWeight: 500,
            color: colorPalette.gray[600],
            marginRight: token.marginSM,
          }}
        >
          Go to page
        </span>
        <InputNumber
          type="number"
          className={`${prefixCls}-custom-quick-jumper-input`}
          value={page}
          onChange={(value) => {
            setPage(value);
          }}
          onKeyDown={(e) => {
            if (e.key === 'Enter') {
              e.preventDefault();
              if (page === null) return;
              const target = Number(page);
              if (Number.isNaN(target)) return;
              const safePage = Math.min(Math.max(1, target), maxPage);
              go(safePage);
              setPage(null);
            }
          }}
          size={isSmall ? 'small' : 'middle'}
          disabled={disabled}
          style={{ width: 72, marginRight: token.marginSM }}
        />
      </div>
    );
  };

  if (process.env.NODE_ENV !== 'production') {
    const warning = devUseWarning('Pagination');

    warning(
      !selectComponentClass,
      'usage',
      '`selectComponentClass` is not official api which will be removed.',
    );
  }

  // ============================= Render =============================
  const iconsProps = React.useMemo<Record<PropertyKey, React.ReactNode>>(() => {
    const ellipsis = <span className={`${prefixCls}-item-ellipsis`}>•••</span>;
    const prevIcon = (
      <button className={`${prefixCls}-item-link`} type="button" tabIndex={-1}>
        {direction === 'rtl' ? <ArrowRight /> : <ArrowLeft />}
      </button>
    );
    const nextIcon = (
      <button className={`${prefixCls}-item-link`} type="button" tabIndex={-1}>
        {direction === 'rtl' ? <ArrowLeft /> : <ArrowRight />}
      </button>
    );
    const jumpPrevIcon = (
      // biome-ignore lint/a11y/useValidAnchor: it is hard to refactor
      <a className={`${prefixCls}-item-link`}>
        <div className={`${prefixCls}-item-container`}>
          {direction === 'rtl' ? (
            <ArrowDoubleRight className={`${prefixCls}-item-link-icon`} />
          ) : (
            <ArrowDoubleLeft className={`${prefixCls}-item-link-icon`} />
          )}
          {ellipsis}
        </div>
      </a>
    );
    const jumpNextIcon = (
      // biome-ignore lint/a11y/useValidAnchor: it is hard to refactor
      <a className={`${prefixCls}-item-link`}>
        <div className={`${prefixCls}-item-container`}>
          {direction === 'rtl' ? (
            <ArrowDoubleLeft className={`${prefixCls}-item-link-icon`} />
          ) : (
            <ArrowDoubleRight className={`${prefixCls}-item-link-icon`} />
          )}
          {ellipsis}
        </div>
      </a>
    );
    return { prevIcon, nextIcon, jumpPrevIcon, jumpNextIcon };
  }, [direction, prefixCls]);

  const selectPrefixCls = getPrefixCls('select', customizeSelectPrefixCls);

  const extendedClassName = classNames(
    {
      [`${prefixCls}-${align}`]: !!align,
      [`${prefixCls}-mini`]: isSmall,
      [`${prefixCls}-rtl`]: direction === 'rtl',
      [`${prefixCls}-bordered`]: token.wireframe,
    },
    contextClassName,
    className,
    rootClassName,
    hashId,
    cssVarCls,
  );

  const mergedStyle: React.CSSProperties = { ...contextStyle, ...style };

  return wrapCSSVar(
    <>
      {token.wireframe && <BorderedStyle prefixCls={prefixCls} />}
      <div
        className={`${prefixCls}-layout`}
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: token.marginSM,
          marginTop: token.marginSM,
          flexWrap: 'wrap',
          ...mergedStyle,
        }}
      >
        {/* SizeChanger */}
        {mergedShowSizeChanger &&
          sizeChangerRender({
            disabled: restProps.disabled ?? false,
            size: mergedPageSize,
            onSizeChange: (_value: string | number) => {
              handlePageSizeChange(Number(_value));
            },
            'aria-label': 'size-changer',
            className: '',
            options:
              mergedPageSizeOptions?.map((opt) => ({
                label: String(opt),
                value: opt,
              })) || [],
          })}

        {/* Pagination */}
        <RcPagination
          {...iconsProps}
          {...restProps}
          prefixCls={prefixCls}
          selectPrefixCls={selectPrefixCls}
          className={extendedClassName}
          locale={locale}
          pageSizeOptions={mergedPageSizeOptions}
          showSizeChanger={false}
          showQuickJumper={false}
          onChange={handlePageChange}
          total={total}
          current={mergedCurrent}
          pageSize={mergedPageSize}
          showTotal={undefined}
        />

        {/* QuickerJump */}
        {showQuickJumper &&
          quickJumperRender({
            disabled: restProps.disabled ?? false,
            go: safeGo,
            maxPage,
          })}
      </div>
    </>,
  );
};

if (process.env.NODE_ENV !== 'production') {
  Pagination.displayName = 'Pagination';
}

export default Pagination;
